import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectReports from './pages/ProjectReports';
import Reports from './pages/Reports';
import ProjectForm from './components/projects/ProjectForm';
import EditProjectForm from './components/projects/EditProjectForm';
import ActionForm from './components/actions/ActionForm';
import EditActionForm from './components/actions/EditActionForm';
import ActionsPage from './pages/ActionsPage';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import CampaignView from './components/campaigns/CampaignView';
import DebriefViewer from './components/debrief/DebriefViewer';

// Target components
import TargetForm from './components/targets/TargetForm';
import TargetList from './components/targets/TargetList';

// Auth & Purple Team components
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BlueTeamDashboard from './pages/BlueTeamDashboard';
import PurpleTeamView from './pages/PurpleTeamView';
import BlueTeamFindingForm from './components/blueteam/BlueTeamFindingForm';
import authService from './services/authService';

// Admin components
import TeamManagement from './pages/TeamManagement';
import TeamsManagement from './pages/TeamsManagement';

// Agent components
import AgentDashboard from './pages/AgentDashboard';
import AgentDetail from './pages/AgentDetail';

// Playbook components
import PlaybookList from './pages/PlaybookList';
import PlaybookBuilder from './pages/PlaybookBuilder';

// Visualization components
import AttackChainView from './pages/AttackChainView';

// User Profile
import UserProfile from './pages/UserProfile';

// Atomic Red Team Library
import AtomicTestLibrary from './pages/AtomicTestLibrary';
import PurpleHQTestManager from './components/atomic/PurpleHQTestManager';

// Settings page
import Settings from './pages/Settings';
import TenantSettings from './pages/TenantSettings';

// Assessment Scheduler
import AssessmentScheduler from './pages/AssessmentScheduler';

// Help/Documentation
import Help from './pages/Help';

// Web Application Testing
import WebTestingListPage from './pages/WebTestingListPage';
import WebTestingPage from './pages/WebTestingPage';

// Vulnerability Scan Import
import VulnScanImport from './pages/VulnScanImport';

// SIEM Alert Import
import SIEMAlertImport from './pages/SIEMAlertImport';
import SIEMReviewPage from './pages/SIEMReviewPage';

// C2 Console
import C2Console from './pages/C2Console';
import { useAuth } from './contexts/AuthContext';

const App = () => {

  return <AppContent />;
};

// Inner component that uses useAuth hook
const AppContent = () => {
  const { user, login, logout, register } = useAuth();

  // Protected route wrapper
  const ProtectedRoute = ({ children, requiredRole }) => {
    if (!user) {
      // Redirect to login if not authenticated
      return <Navigate to="/login" />;
    }

    if (requiredRole) {
      const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      if (!allowedRoles.includes(user.role) && user.role !== 'admin' && user.role !== 'purple_team') {
        return <Navigate to="/" />;
      }
    }

    return children;
  };

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} />
      <Routes>
        {/* Auth routes - no layout */}
        <Route path="/login" element={<Login onLogin={login} />} />
        <Route path="/register" element={<Register onRegister={register} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Main app routes with layout */}
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout user={user} onLogout={logout}>
                <Routes>
                  {/* Admin-only Routes */}
                  <Route path="/" element={
                    <ProtectedRoute requiredRole={['admin']}>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/executive" element={
                    <ProtectedRoute requiredRole={['admin']}>
                      <ExecutiveDashboard />
                    </ProtectedRoute>
                  } />

                  {/* Project Routes */}
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/new" element={<ProjectForm />} />
                  <Route path="/projects/:projectId" element={<ProjectDetail />} />
                  <Route path="/projects/:projectId/edit" element={<EditProjectForm />} />
                  <Route path="/projects/:projectId/reports" element={<ProjectReports />} />
                  <Route path="/projects/:projectId/actions" element={<ActionsPage />} />
                  <Route path="/projects/:projectId/actions/new" element={<ActionForm />} />
                  <Route path="/projects/:projectId/actions/:actionId/edit" element={<EditActionForm />} />
                  <Route path="/projects/:projectId/targets" element={<TargetList />} />
                  <Route path="/projects/:projectId/targets/new" element={<TargetForm />} />
                  <Route path="/projects/:projectId/targets/:targetId/edit" element={<TargetForm isEditing={true} />} />
                  <Route path="/projects/:projectId/campaigns" element={<CampaignView />} />
                  <Route path="/projects/:projectId/debrief" element={<DebriefViewer />} />
                  <Route path="/projects/:projectId/web-testing" element={<WebTestingListPage />} />
                  <Route path="/projects/:projectId/web-testing/:webTargetId" element={<WebTestingPage />} />
                  <Route path="/projects/:projectId/c2" element={
                    <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin', 'c2_operator', 'c2_lead', 'c2_analyst']}>
                      <C2Console />
                    </ProtectedRoute>
                  } />
                  <Route path="/reports" element={<Reports />} />

                {/* Blue Team Routes - project-specific */}
                <Route path="/blue-team" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <BlueTeamDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/blue-team" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <BlueTeamDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/blue-team/findings/new" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <BlueTeamFindingForm />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/blue-team/findings/:findingId" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <BlueTeamFindingForm isEditing={true} />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/blue-team/campaigns" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <CampaignView mode="blue" />
                  </ProtectedRoute>
                } />

                {/* Purple Team Routes - project-specific */}
                <Route path="/purple-team" element={
                  <ProtectedRoute requiredRole={['purple_team', 'admin']}>
                    <PurpleTeamView />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/purple-team" element={
                  <ProtectedRoute requiredRole={['purple_team', 'admin']}>
                    <PurpleTeamView />
                  </ProtectedRoute>
                } />

                {/* Agent Routes */}
                <Route path="/agents" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AgentDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/agents/:agentId" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AgentDetail />
                  </ProtectedRoute>
                } />

                {/* C2 Console Route */}
                <Route path="/c2" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin', 'c2_operator', 'c2_lead', 'c2_analyst']}>
                    <C2Console />
                  </ProtectedRoute>
                } />

                {/* Playbook Routes */}
                <Route path="/playbooks" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <PlaybookList />
                  </ProtectedRoute>
                } />
                <Route path="/playbooks/new" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <PlaybookBuilder />
                  </ProtectedRoute>
                } />
                <Route path="/playbooks/:playbookId" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <PlaybookBuilder />
                  </ProtectedRoute>
                } />

                {/* Attack Chain Visualization Routes */}
                <Route path="/attack-chain" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AttackChainView />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/attack-chain" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AttackChainView />
                  </ProtectedRoute>
                } />

                {/* Atomic Red Team Library */}
                <Route path="/atomic-tests" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AtomicTestLibrary />
                  </ProtectedRoute>
                } />

                {/* PurpleHQ Custom Tests */}
                <Route path="/purplehq-tests" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <PurpleHQTestManager />
                  </ProtectedRoute>
                } />

                {/* Vulnerability Scan Import */}
                <Route path="/projects/:projectId/import-scan" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <VulnScanImport />
                  </ProtectedRoute>
                } />

                {/* SIEM Alert Import */}
                <Route path="/projects/:projectId/blue-team/import" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <SIEMAlertImport />
                  </ProtectedRoute>
                } />

                {/* SIEM Review Page */}
                <Route path="/projects/:projectId/blue-team/siem-review" element={
                  <ProtectedRoute requiredRole={['blue_team', 'purple_team', 'admin']}>
                    <SIEMReviewPage />
                  </ProtectedRoute>
                } />

                {/* User Profile Route */}
                <Route path="/profile" element={<UserProfile />} />

                {/* Admin Routes */}
                <Route path="/admin/users" element={
                  <ProtectedRoute requiredRole={['admin']}>
                    <TeamManagement />
                  </ProtectedRoute>
                } />
                <Route path="/admin/teams" element={
                  <ProtectedRoute requiredRole={['admin']}>
                    <TeamsManagement />
                  </ProtectedRoute>
                } />

                {/* Settings Routes */}
                <Route path="/settings" element={
                  <ProtectedRoute requiredRole={['admin']}>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/settings/:tab" element={
                  <ProtectedRoute requiredRole={['admin']}>
                    <Settings />
                  </ProtectedRoute>
                } />

                {/* Tenant Settings Route */}
                <Route path="/tenant-settings" element={
                  <ProtectedRoute requiredRole={['tenant_admin', 'admin']}>
                    <TenantSettings />
                  </ProtectedRoute>
                } />

                {/* Assessment Scheduler Routes */}
                <Route path="/scheduler" element={
                  <ProtectedRoute requiredRole={['red_team', 'purple_team', 'admin']}>
                    <AssessmentScheduler />
                  </ProtectedRoute>
                } />

          {/* Help/Documentation Route */}
          <Route path="/help" element={<Help />} />
        </Routes>
      </Layout>
    </ProtectedRoute>
  } />
  </Routes>
    </Router>
  );
};

// Wrap App with all context providers
const AppWithProviders = () => (
  <ThemeProvider>
    <LanguageProvider>
      <BrandingProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrandingProvider>
    </LanguageProvider>
  </ThemeProvider>
);

export default AppWithProviders;
